// components/Ads/AdUnit.tsx
import React, { useEffect, useRef } from 'react';
import { AD_CLIENT_ID } from './adConfig';

interface AdUnitProps {
  slot: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'leaderboard';
  className?: string;
  style?: React.CSSProperties;
}

const AdUnit: React.FC<AdUnitProps> = ({
  slot,
  format = 'auto',
  className = '',
  style = {}
}) => {
  const adRef = useRef<HTMLDivElement>(null);
  const hasPushed = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!adRef.current || hasPushed.current) return;

    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const pushWithRetry = (attempt = 0) => {
      if (hasPushed.current) return;

      if (window.adsbygoogle) {
        try {
          hasPushed.current = true;
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (error) {
          console.warn('Ad push warning:', error);
        }
        return;
      }

      if (attempt < 5) {
        retryTimer = setTimeout(() => pushWithRetry(attempt + 1), 250);
      }
    };

    const initAd = () => {
      try {
        if (!adRef.current || hasPushed.current) return;

        const adId = `ad-${slot}-${Date.now()}`;
        const insElement = document.createElement('ins');
        insElement.className = 'adsbygoogle';
        insElement.id = adId;
        insElement.style.display = 'block';
        insElement.style.overflow = 'hidden';
        insElement.style.width = '100%';

        if (format === 'rectangle') {
          insElement.style.minHeight = '250px';
        } else if (format === 'leaderboard') {
          insElement.style.minHeight = '90px';
        } else {
          insElement.style.minHeight = '100px';
        }

        insElement.setAttribute('data-ad-client', AD_CLIENT_ID);
        insElement.setAttribute('data-ad-slot', slot);
        insElement.setAttribute('data-ad-format', format);
        insElement.setAttribute('data-full-width-responsive', 'true');

        adRef.current.innerHTML = '';
        adRef.current.appendChild(insElement);

        setTimeout(() => pushWithRetry(0), 100);
      } catch (error) {
        console.error('Ad initialization error:', error);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasPushed.current) {
          initAd();
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observer.observe(adRef.current);

    const fallbackTimer = setTimeout(() => {
      if (!hasPushed.current) {
        initAd();
        observer.disconnect();
      }
    }, 2000);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [slot, format]);

  const containerStyle: React.CSSProperties = {
    minHeight: format === 'rectangle' ? '250px' : (format === 'leaderboard' ? '90px' : '100px'),
    width: '100%',
    maxWidth: format === 'rectangle' ? '336px' : (format === 'leaderboard' ? '728px' : '100%'),
    margin: '0 auto',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    overflow: 'hidden',
    position: 'relative',
    ...style
  };

  return (
    <div
      ref={adRef}
      className={`ad-unit ${className}`}
      style={containerStyle}
      data-slot={slot}
      data-format={format}
    />
  );
};

export default React.memo(AdUnit);
